import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { Dayjs } from 'dayjs'
import { Button, Grid, TextField, useTheme, useMediaQuery, Typography, Box, Radio, RadioGroup, FormControlLabel, FormControl } from '@mui/material'

import Env from '../../shared/config/envConfig'
import CheckboxComponent from '../../shared/components/FormComponents/CheckboxComponent'
import DropDownComponent, { EnumElement } from '../../shared/components/FormComponents/DropDownComponent'
import LabelWithTooltipComponent from '../../shared/components/FormComponents/LabelWithTooltipComponent'
import { useHistory } from 'react-router-dom'
import { setMessage } from '../../store/settings/settings.actions'
import { Severity } from '@3m5/crude-frontend/dist/shared/types/messageTypes'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { getEventSettings } from '../../store/settings/settings.selectors'

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const [year, month, day] = dateString.split('-')
  return `${day}.${month}.${year}`
}

const Registration = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const history = useHistory()
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const { fridayDate, saturdayDate, sundayDate, helferpartyDate, helferpartyLocation } = useSelector(getEventSettings)

  // EmploymentList
  const [areaList, setAreaList] = useState<EnumElement[]>([])

  // required fields
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [telephoneNumber, setTelephoneNumber] = useState<string>('')
  const [emailAddress, setEmailAddress] = useState<string>('')
  const [residentialAddress, setResidentialAddress] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [gender, setGender] = useState<EnumElement |undefined>()
  const [preferedArea, setPreferedArea] = useState<EnumElement |undefined>()
  const [birthYear, setBirthYear] = useState<string>('')

  // optional
  const [fridayFrom, setFridayFrom] = React.useState<Dayjs | null>(null)
  const [fridayTo, setFridayTo] = React.useState<Dayjs | null>(null)
  const [saturdayFrom, setSaturdayFrom] = React.useState<Dayjs | null>(null)
  const [saturdayTo, setSaturdayTo] = React.useState<Dayjs | null>(null)
  const [sundayFrom, setSundayFrom] = React.useState<Dayjs | null>(null)
  const [sundayTo, setSundayTo] = React.useState<Dayjs | null>(null)

  // at least one
  const [availableOnFriday, setAvailableOnFriday] = useState<boolean>(false)
  const [availableOnSaturday, setAvailableOnSaturday] = useState<boolean>(false)
  const [availableOnSunday, setAvailableOnSunday] = useState<boolean>(false)

  // other fields
  const [friend, setFriend] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [helpedBefore, setHelpedBefore] = useState<boolean>(false)
  const [isClubMember, setIsClubMember] = useState<boolean>(false)
  const [participateInParty, setParticipateInParty] = useState<boolean>(false)
  const [helferpartyParticipants, setHelferpartyParticipants] = useState<number>(0)
  const [iban, setIban] = useState<string>('')
  const [bic, setBic] = useState<string>('')

  // helper
  const [disabled, setDisabled] = useState<boolean>(true)
  const [showHint, setShowHint] = useState<boolean>(false)
  const [showRequiredHints, setShowRequiredHints] = useState<boolean>(false)
  const [showYearHint, setShowYearHint] = useState<boolean>(false)
  const [showEmailHint, setShowEmailHint] = useState<boolean>(false)
  const [showTimeHint, setShowTimeHint] = useState<boolean>(false)
  const [showIbanHint, setShowIbanHint] = useState<boolean>(false)
  const [showBicHint, setShowBicHint] = useState<boolean>(false)

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .trim()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      )
  }

  const validateIban = (iban: string) => {
    if (!iban) return true
    const cleanIban = iban.replace(/\s+/g, '').toUpperCase()
    if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{4,30}$/.test(cleanIban)) return false

    const rearranged = cleanIban.substring(4) + cleanIban.substring(0, 4)
    const numeric = rearranged.split('').map(char => {
      const code = char.charCodeAt(0)
      if (code >= 65 && code <= 90) return (code - 55).toString()
      return char
    }).join('')

    let remainder = 0
    for (let i = 0; i < numeric.length; i += 7) {
      const chunk = remainder.toString() + numeric.substring(i, i + 7)
      remainder = parseInt(chunk, 10) % 97
    }
    return remainder === 1
  }

  const validateBic = (bic: string) => {
    if (!bic) return true
    return /^[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?$/.test(bic.toUpperCase().trim())
  }

  const formatIban = (value: string) => {
    const cleanValue = value.replace(/\s+/g, '').toUpperCase()
    let formatted = ''
    for (let i = 0; i < cleanValue.length; i++) {
      if (i > 0 && i % 4 === 0) {
        formatted += ' '
      }
      formatted += cleanValue[i]
    }
    return formatted
  }

  const checkValidity = () => {
    let temp = true
    if (availableOnFriday || availableOnSaturday || availableOnSunday) {
      temp = false
    }
    if (!firstName || !lastName || !telephoneNumber || !emailAddress || !residentialAddress || !gender || !birthYear) {
      temp = true
    }
    if (parseInt(birthYear, 10) < 1900 || parseInt(birthYear, 10) > new Date().getFullYear()) {
      temp = true
    }
    if (emailAddress && !validateEmail(emailAddress)) {
      temp = true
    }
    if (fridayFrom?.isAfter(fridayTo) || saturdayFrom?.isAfter(saturdayTo) || sundayFrom?.isAfter(sundayTo)) {
      temp = true
    }
    if (participateInParty && (isNaN(parseInt(helferpartyParticipants, 10)) || parseInt(helferpartyParticipants, 10) < 0)) {
      temp = true
    }
    if (iban && !validateIban(iban)) {
      temp = true
    }
    if (bic && !validateBic(bic)) {
      temp = true
    }
    setDisabled(temp)
  }

  useEffect(() => {
    checkValidity()
  }, [firstName, lastName, telephoneNumber, emailAddress, residentialAddress, gender, birthYear, participateInParty, helferpartyParticipants, iban, bic])

  useEffect(() => {
    checkValidity()
    if (availableOnFriday || availableOnSaturday || availableOnSunday) {
      setShowHint(false)
    }
  }, [availableOnFriday, availableOnSaturday, availableOnSunday])

  useEffect(() => {
    checkValidity()
    if (parseInt(birthYear, 10) < 1900 || parseInt(birthYear, 10) > new Date().getFullYear()) {
      setShowYearHint(true)
    } else {
      setShowYearHint(false)
    }
  }, [birthYear])

  useEffect(() => {
    checkValidity()
    if (emailAddress && !validateEmail(emailAddress)) {
      setShowEmailHint(true)
    } else {
      setShowEmailHint(false)
    }
  }, [emailAddress])

  useEffect(() => {
    checkValidity()
    if (fridayFrom?.isAfter(fridayTo) || saturdayFrom?.isAfter(saturdayTo) || sundayFrom?.isAfter(sundayTo)) {
      setShowTimeHint(true)
    } else {
      setShowTimeHint(false)
    }
  }, [fridayFrom, fridayTo, saturdayFrom, saturdayTo, sundayFrom, sundayTo])

  useEffect(() => {
    checkValidity()
    if (iban && !validateIban(iban)) {
      setShowIbanHint(true)
    } else {
      setShowIbanHint(false)
    }
  }, [iban])

  useEffect(() => {
    checkValidity()
    if (bic && !validateBic(bic)) {
      setShowBicHint(true)
    } else {
      setShowBicHint(false)
    }
  }, [bic])

  const formatTime = (date: Dayjs | null) => {
    return date ? date.format('HH:mm') : null
  }

  const registerHelper = () => {
    if (city) {
      console.warn('Honeypot field filled. Submission ignored.')
      history.push('/thankyou')
      return
    }
    const basePath = Env.basePath
    const restUrl = `${basePath}/api/import/helper`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')
    const newHelper = {
      helper: {
        firstName,
        lastName,
        birthYear,
        emailAddress,
        telephoneNumber,
        gender: gender?.key,
        address: residentialAddress,
        availableOnFriday,
        availableOnSaturday,
        availableOnSunday,
        fridayFrom: formatTime(fridayFrom),
        fridayTo: formatTime(fridayTo),
        saturdayFrom: formatTime(saturdayFrom),
        saturdayTo: formatTime(saturdayTo),
        sundayFrom: formatTime(sundayFrom),
        sundayTo: formatTime(sundayTo),
        friend,
        helpedBefore,
        isClubMember,
        iban: iban.replace(/\s+/g, '').toUpperCase(),
        bic: bic.replace(/\s+/g, '').toUpperCase(),
        notes,
        preferedArea: preferedArea ? (preferedArea.key === 'null' ? null : preferedArea.key) : null,
        helferpartyParticipants: (participateInParty && helferpartyParticipants === 0) ? 1 : 0,
      },
    }
    fetch(restUrl,
      {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
        body: JSON.stringify(newHelper),
      })
      .then(async res => {
        if (res.ok) {
          history.push('/thankyou')
        } else if (res.status === 409) {
          dispatch(setMessage({
            severity: Severity.error,
            message: 'Diese E-Mail Adresse ist bereits als Helfer registriert.',
          }))
        } else {
          dispatch(setMessage({
            severity: Severity.error,
            message: 'Etwas ist schief gegangen! Versuche es zu einem späteren Zeitpunkt erneut.',
          }))
        }
      })
      .catch(() => {
        dispatch(setMessage({
          severity: Severity.error,
          message: 'Etwas ist schief gegangen! Versuche es zu einem späteren Zeitpunkt erneut.',
        }))
      })
  }

  const getAreaList = () => {
    const basePath = Env.basePath
    const restUrl = `${basePath}/api/import/areas`
    const requestHeaders: HeadersInit = new Headers()
    requestHeaders.set('Accept', 'application/json')
    requestHeaders.set('Content-Type', 'application/json')

    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then((body) => {
        const list: EnumElement[] = [{
          key: 'null',
          value: 'Sonstige',
        }]
        body.areas.forEach((area: any) => {
          list.push({
            key: area.id,
            value: `${area.displayName}`,
          })
        })
        setAreaList(list)
      })
      .catch(() => {
        dispatch(setMessage({
          severity: Severity.error,
          message: 'Etwas ist schief gegangen! Versuche es zu einem späteren Zeitpunkt erneut.',
        }))
      })
  }

  useEffect(() => {
    getAreaList()
  }, [],
  )

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='de'>
      <Box sx={{ margin: '50px' }}>
        <Typography sx={{ marginTop: '20px' }} variant='body1'>Mit * markierte Pflichtfelder sind im Anmeldeformular
          auszufüllen
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}><Typography sx={{ marginTop: '20px' }} variant='h5'>Allgemeine Informationen</Typography>
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              required
              error={showRequiredHints && !firstName}
              helperText={showRequiredHints && !firstName ? 'Vorname ist ein Pflichfeld' : ''}
              fullWidth
              id='firstname'
              label='Vorname'
              name='firstname'
              value={firstName}
              onChange={(event) => setFirstName(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              required
              fullWidth
              error={showRequiredHints && !lastName}
              helperText={showRequiredHints && !lastName ? 'Nachname ist ein Pflichfeld' : ''}
              id='laststname'
              label='Nachname'
              name='laststname'
              value={lastName}
              onChange={(event) => setLastName(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              required
              fullWidth
              error={(showRequiredHints && !emailAddress) || showEmailHint}
              helperText={showRequiredHints && !emailAddress ? 'E-Mail ist ein Pflichfeld' : (showEmailHint ? 'Die eingegebene E-Mail-Adresse ist nicht gültig' : '')}
              id='email'
              label='E-Mail'
              name='email'
              value={emailAddress}
              onChange={(event) => setEmailAddress(event.currentTarget.value.trim())}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              required
              fullWidth
              id='phone'
              error={showRequiredHints && !telephoneNumber}
              helperText={showRequiredHints && !telephoneNumber ? 'Telefon ist ein Pflichfeld' : ''}
              label='Telefon'
              name='phone'
              value={telephoneNumber}
              onChange={(event) => setTelephoneNumber(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin='normal'
              required
              fullWidth
              error={showRequiredHints && !residentialAddress}
              helperText={showRequiredHints && !residentialAddress ? 'Wohnort ist ein Pflichfeld' : ''}
              id='city_real'
              label='Wohnort'
              name='city_real'
              value={residentialAddress}
              onChange={(event) => setResidentialAddress(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={12} sx={{ display: 'none' }} aria-hidden='true'>
            <TextField
              margin='normal'
              fullWidth
              id='city'
              label='City'
              name='city'
              value={city}
              onChange={(event) => setCity(event.currentTarget.value)}
              autoComplete='off'
              tabIndex={-1}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TextField
              margin='normal'
              required
              fullWidth
              error={(showRequiredHints && !birthYear) || showYearHint}
              helperText={showRequiredHints && !birthYear ? 'Geburtsjahr ist ein Pflichfeld' : (showYearHint ? 'Bitte nur das Geburtsjahr als vierstellige Zahl eintragen' : '')}
              id='birthYear'
              label='Geburtsjahr'
              name='birthYear'
              value={birthYear}
              onChange={(event) => setBirthYear(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <DropDownComponent
              fullWidth
              error={showRequiredHints && !gender}
              helperText={showRequiredHints && !gender ? 'Geschlecht ist ein Pflichfeld' : ''}
              options={[
                {
                  key: 'Male',
                  value: 'Mann',
                },
                {
                  key: 'Female',
                  value: 'Frau',
                },
                {
                  key: 'Diverse',
                  value: 'Divers',
                },
              ]}
              disableClearable
              value={gender}
              label='Geschlecht'
              name='gender'
              onChange={(value) => setGender(value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            {areaList.length > 0 &&
              <DropDownComponent
                fullWidth
                options={areaList}
                disableClearable
                value={preferedArea}
                label='Bevorzugter Bereich'
                name='preferedArea'
                onChange={(value) => setPreferedArea(value)}
              />}
          </Grid>
          <Grid item xs={12}><Typography sx={{ marginTop: '20px' }} variant='h5'>Einsatzzeiten</Typography> </Grid>
          {showHint &&
            <Typography sx={{ color: theme.palette.primary.main, marginTop: '10px', marginLeft: '16px' }} variant='body1'>Mindestens ein Tag muss ausgewählt sein!</Typography>}
          <Grid item xs={downMd ? 12 : 4}>
            <CheckboxComponent
              checked={availableOnFriday}
              id='availableOnFriday'
              name='availableOnFriday'
              label={`Einsatz am Freitag, den ${formatDate(fridayDate)}`}
              onChange={(value) => setAvailableOnFriday(value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TimePicker
              label='von'
              value={fridayFrom}
              onChange={(value) => setFridayFrom(value)}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TimePicker
              label='bis'
              value={fridayTo}
              minTime={fridayFrom}
              onChange={(value) => setFridayTo(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={showTimeHint && availableOnFriday && fridayFrom?.isAfter(fridayTo)}
                  helperText={showTimeHint && availableOnFriday && fridayFrom?.isAfter(fridayTo) ? 'Endzeit muss nach der Anfangszeit liegen' : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <CheckboxComponent
              checked={availableOnSaturday}
              id='availableOnSaturday'
              name='availableOnSaturday'
              label={`Einsatz am Samstag, den ${formatDate(saturdayDate)}`}
              onChange={(value) => setAvailableOnSaturday(value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TimePicker
              label='von'
              value={saturdayFrom}
              onChange={(value) => setSaturdayFrom(value)}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TimePicker
              label='bis'
              value={saturdayTo}
              minTime={saturdayFrom}
              onChange={(value) => setSaturdayTo(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={showTimeHint && availableOnSaturday && saturdayFrom?.isAfter(saturdayTo)}
                  helperText={showTimeHint && availableOnSaturday && saturdayFrom?.isAfter(saturdayTo) ? 'Endzeit muss nach der Anfangszeit liegen' : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <CheckboxComponent
              checked={availableOnSunday}
              id='availableOnSunday'
              name='availableOnSunday'
              label={`Einsatz am Sonntag, den ${formatDate(sundayDate)}`}
              onChange={(value) => setAvailableOnSunday(value)}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TimePicker
              label='von'
              value={sundayFrom}
              onChange={(value) => setSundayFrom(value)}
              renderInput={(params) => <TextField {...params} />}
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 4}>
            <TimePicker
              label='bis'
              value={sundayTo}
              minTime={saturdayFrom}
              onChange={(value) => setSundayTo(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={showTimeHint && availableOnSunday && sundayFrom?.isAfter(sundayTo)}
                  helperText={showTimeHint && availableOnSunday && sundayFrom?.isAfter(sundayTo) ? 'Endzeit muss nach der Anfangszeit liegen' : ''}
                />
              )}
            />
          </Grid>
          <Grid item xs={12}><Typography sx={{ marginTop: '20px' }} variant='h5'>Helferparty</Typography> </Grid>
          <Grid item xs={12}>
            <Typography variant='body1'>
              Unsere Helferparty findet am {formatDate(helferpartyDate)} in {helferpartyLocation} statt. Möchtest du teilnehmen?
            </Typography>
            <FormControl component='fieldset' sx={{ marginTop: '10px' }}>
              <RadioGroup
                value={participateInParty ? 'yes' : 'no'}
                onChange={(event) => {
                  const val = event.target.value === 'yes'
                  setParticipateInParty(val)
                  if (!val) {
                    setHelferpartyParticipants(0)
                  }
                }}
              >
                <FormControlLabel
                  value='yes' control={<Radio />} label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography sx={{ marginRight: '10px' }}>Ich nehme teil sowie</Typography>
                      <TextField
                        size='small'
                        type='number'
                        value={helferpartyParticipants}
                        disabled={!participateInParty}
                        onChange={(event) => setHelferpartyParticipants(parseInt(event.target.value, 10))}
                        sx={{ width: '80px' }}
                        inputProps={{ min: 0 }}
                      />
                      <Typography sx={{ marginLeft: '10px' }}>weitere Personen.</Typography>
                    </Box>
                  }
                />
                <FormControlLabel value='no' control={<Radio />} label='Ich nehme nicht teil.' />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12}><Typography sx={{ marginTop: '20px' }} variant='h5'>Weitere Informationen</Typography>
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              fullWidth
              error={showIbanHint}
              helperText={showIbanHint ? 'Ungültige IBAN' : ''}
              id='iban'
              label='IBAN (optional)'
              name='iban'
              value={iban}
              onChange={(event) => setIban(formatIban(event.currentTarget.value))}
            />
            <Typography variant='body2' sx={{ fontStyle: 'italic', marginTop: '5px' }}>
              Diese Angaben benötigen wir ausschließlich für die Überweisung deiner Helfer-Vergütung nach dem Event.
            </Typography>
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              fullWidth
              error={showBicHint}
              helperText={showBicHint ? 'Ungültiger BIC' : ''}
              id='bic'
              label='BIC (optional)'
              name='bic'
              value={bic}
              onChange={(event) => setBic(event.currentTarget.value.replace(/\s+/g, '').toUpperCase())}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxComponent
              checked={helpedBefore}
              id='helpedBefore'
              name='availableOnSunday'
              label='Ich habe schon mal beim Schloss-Triathlon Moritzburg geholfen'
              onChange={(value) => setHelpedBefore(value)}
            />
          </Grid>
          <Grid item xs={12}>
            <CheckboxComponent
              checked={isClubMember}
              id='isClubMember'
              name='isClubMember'
              label='Ich bin ein offizielles Vereinsmitglied des Moritzburger Triathlonverein e.V.'
              onChange={(value) => setIsClubMember(value)}
            />
          </Grid>
          <Grid item xs={12} sx={{ marginTop: '20px' }}>
            <LabelWithTooltipComponent
              label='Eine/Ein Freundin/Freund wird auch mithelfen. Wir möchten gern gemeinsam eingeteilt werden.'
            />
          </Grid>
          <Grid item xs={downMd ? 12 : 6}>
            <TextField
              margin='normal'
              fullWidth
              id='friend'
              label='Name des Freundes'
              name='friend'
              value={friend}
              onChange={(event) => setFriend(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              margin='normal'
              fullWidth
              multiline
              minRows={3}
              id='notes'
              label='Besondere Fähigkeiten und Wünsche'
              name='notes'
              value={notes}
              onChange={(event) => setNotes(event.currentTarget.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                onClick={() => {
                  if (disabled) {
                    if (availableOnFriday || availableOnSaturday || availableOnSunday) {
                      setShowHint(false)
                    } else {
                      setShowHint(true)
                    }
                    if (parseInt(birthYear, 10) < 1900 || parseInt(birthYear, 10) > new Date().getFullYear()) {
                      setShowYearHint(true)
                    } else {
                      setShowYearHint(false)
                    }
                    if (emailAddress && !validateEmail(emailAddress)) {
                      setShowYearHint(true)
                    } else {
                      setShowYearHint(false)
                    }
                    if (!firstName || !lastName || !telephoneNumber || !emailAddress || !residentialAddress || !gender || !birthYear) {
                      setShowRequiredHints(true)
                    } else {
                      setShowRequiredHints(false)
                    }
                  } else {
                    registerHelper()
                  }
                }}
              >
                Anmelden
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </LocalizationProvider>
  )
}
export default Registration
